import { useState, useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, AlertModal } from '@components/ui';
import DataTable from '@components/shared/DataTable/DataTable';
import type {
  DataTableColumn,
  SortState,
} from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { getLegalEntities, deleteLegalEntity } from '@/services/legalEntityService';
import { getEmploymentTypes } from '@/services/employmentTypeService';
import { useNotificationStore } from '@stores/notificationStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import { useCountries } from '@/hooks/useCountries';
import type { LegalEntityListItem } from '@/types/legalEntity';
import type { FilterFieldConfig } from '@/types/filters';
import { WorkpayApiError } from '@lib/apiEnvelope';
import PlusIcon from '@assets/icons/PlusIcon';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import { validateTableSearch, buildSortString, toPaginationState, serializeFilters } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import CreateEntityModal from '@features/entities/CreateEntityModal/CreateEntityModal';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { useAuditTrigger } from '@/hooks/useAuditTrigger';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from '@/hooks/useTranslation';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import styles from './index.module.scss';

export const Route = createFileRoute('/w/entities/')({
  validateSearch: (search: Record<string, unknown>) =>
    validateTableSearch(search, { sortBy: 'created', sortDir: 'desc' }),
  component: EntitiesPage,
});

function StatusBadge({ status }: { status: LegalEntityListItem['status'] }) {
  const className = status === 'ACTIVE' ? styles.badgeActive : styles.badgeInactive;
  return <span className={`${styles.badge} ${className}`}>{status}</span>;
}

function EntitiesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = Route.useSearch();

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  const { data: employmentTypesData } = useQuery({
    queryKey: ['employmentTypes', { size: 100 }],
    queryFn: () => getEmploymentTypes({ size: 100 }),
  });

  const employmentTypeMap = useMemo(
    () => new Map((employmentTypesData?.content ?? []).map((et) => [et.id, et.name])),
    [employmentTypesData],
  );

  const { countries } = useCountries();

  // Filter fields ordered to match table columns:
  // Entity Name, Country, Entity Type, ..., Status, Created
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'name',
        label: t('K_FILTER_ENTITY_NAME', 'Entity Name'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_ENTITY_NAME', 'Search entity name...'),
      },
      {
        fieldKey: 'country.id',
        label: t('K_FILTER_COUNTRY', 'Country'),
        type: 'select',
        options: countries.map((c) => ({ label: c.name, value: c.id })),
      },
      {
        fieldKey: 'entityType',
        label: t('K_FILTER_ENTITY_TYPE', 'Entity Type'),
        type: 'select',
        options: [
          { label: t('K_OPTION_WORKPAY_OWNED', 'Workpay Owned'), value: 'WORKPAY_OWNED' },
          { label: t('K_OPTION_PARTNER', 'Partner'), value: 'PARTNER' },
        ],
      },
      {
        fieldKey: 'status',
        label: t('K_FILTER_STATUS', 'Status'),
        type: 'select',
        options: [
          { label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')), value: 'ACTIVE' },
          { label: toTitleCase(t('K_STATUS_INACTIVE', 'Inactive')), value: 'INACTIVE' },
        ],
      },
      {
        fieldKey: 'createdAt',
        label: t('K_FILTER_CREATED', 'Created Date'),
        type: 'date-range',
        fromLabel: t('K_LABEL_FROM', 'From'),
        toLabel: t('K_LABEL_TO', 'To'),
      },
    ],
    [t, countries],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo: '/w/entities',
    filtersParam,
    fields: filterFields,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deactivateItem, setDeactivateItem] = useState<LegalEntityListItem | null>(null);
  const audit = useAuditTrigger();
  // Pin the opener identity in row deps so the row tree doesn't re-render on
  // modal open/close (the whole `audit` object changes when `target` flips).
  const auditOpen = audit.open;

  const handleCreate = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => deleteLegalEntity(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['legalEntities'] });
      setDeactivateItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
      setDeactivateItem(null);
    },
  });

  const columns = useMemo<DataTableColumn<LegalEntityListItem>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_ENTITY_NAME', 'Entity Name'),
        sortable: true,
        sortKey: 'name',
        width: '200px',
        cell: (item) => (
          <span className={styles.cellPrimary} title={item.name || '-'}>{item.name || '-'}</span>
        ),
      },
      {
        id: 'country',
        header: t('K_COLUMN_COUNTRY', 'Country'),
        cell: (item) => item.country?.name || '-',
      },
      {
        id: 'entityType',
        header: t('K_COLUMN_ENTITY_TYPE', 'Entity Type'),
        cell: (item) => {
          if (item.entityType === 'WORKPAY_OWNED') return t('K_OPTION_WORKPAY_OWNED', 'Workpay Owned');
          if (item.entityType === 'PARTNER') return t('K_OPTION_PARTNER', 'Partner');
          return '-';
        },
      },
      {
        id: 'employmentTypes',
        header: t('K_COLUMN_EMPLOYMENT_TYPES', 'Employment Types'),
        cell: (item) => {
          const names = (item.employmentTypeIds ?? [])
            .map((id) => employmentTypeMap.get(id))
            .filter(Boolean);
          return names.length > 0 ? names.join(', ') : '-';
        },
      },
      {
        id: 'effectiveFrom',
        header: t('K_COLUMN_EFFECTIVE_FROM', 'Effective From'),
        cell: (item) =>
          item.effectiveFrom
            ? new Date(item.effectiveFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
            : '-',
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (item) => <StatusBadge status={item.status} />,
      },
      {
        id: 'created',
        header: t('K_COLUMN_CREATED', 'Created'),
        sortable: true,
        sortKey: 'createdAt',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {new Date(item.createdAt).toLocaleDateString()}
          </span>
        ),
      },
      {
        id: 'updated',
        header: t('K_COLUMN_UPDATED', 'Updated'),
        sortable: true,
        sortKey: 'updatedAt',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {new Date(item.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [t, employmentTypeMap],
  );

  const rowActions = useCallback((item: LegalEntityListItem) => {
    const actions: ProfileAction[] = [];
    if (hasPermission('ENTITIES_UPDATE') && item.status !== 'INACTIVE') {
      actions.push({
        id: 'edit',
        label: t('K_EDIT', 'Edit'),
        onClick: () =>
          navigate({
            to: '/w/entities/profile/$entityId',
            params: { entityId: item.id },
            search: { edit: true },
          }),
      });
    }
    actions.push({
      id: 'viewEmployees',
      label: t('K_BUTTON_VIEW_ALL_EMPLOYEES', 'View All Employees'),
      onClick: () =>
        navigate({
          to: '/w/employees',
          search: {
            tab: 'ALL',
            filters: serializeFilters({ legalEntityId: item.id }),
          },
        }),
    });
    if (hasPermission('ENTITIES_DELETE') && item.status === 'ACTIVE') {
      actions.push({ id: 'deactivate', label: t('K_DEACTIVATE', 'Deactivate'), onClick: () => setDeactivateItem(item) });
    }
    if (hasPermission('AUDIT_READ')) {
      actions.push({
        id: 'audit',
        label: t('K_AUDIT_TITLE', 'Audit trail'),
        onClick: () => auditOpen(item.id, item.name || undefined),
      });
    }
    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  }, [t, hasPermission, navigate, auditOpen]);

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/w/entities',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/w/entities',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/w/entities',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['legalEntities', { page, size, sort: sortString, filterPayload }],
    queryFn: () => getLegalEntities({ page, size, sort: sortString }, filterPayload),
  });

  const entities = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const getRowKey = useCallback((item: LegalEntityListItem) => item.id, []);

  const handleRowClick = useCallback(
    (item: LegalEntityListItem) => {
      void navigate({ to: '/w/entities/profile/$entityId', params: { entityId: item.id } });
    },
    [navigate],
  );

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{t('K_EMPTY_NO_ENTITIES', 'No entities yet')}</h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_CREATE_FIRST_ENTITY', 'Get started by creating your first legal entity.')}
          </p>
          {hasPermission('ENTITIES_CREATE') && (
            <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
              {t('K_BUTTON_CREATE_ENTITY', 'Create an entity')}
            </Button>
          )}
        </div>
      ),
    [listForbidden, handleCreate, t, hasPermission],
  );

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_ENTITIES', 'Legal Entities')}</h1>
          <p className={styles.subtitle}>{t('K_PAGE_SUBTITLE_ENTITIES', 'Manage all your legal entities')}</p>
        </div>

        {hasPermission('ENTITIES_CREATE') && (
          <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
            {t('K_BUTTON_CREATE_ENTITY', 'Create an entity')}
          </Button>
        )}
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={entities}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        loadingText={t('K_LOADING_ENTITIES', 'Loading entities...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="Entities list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      <CreateEntityModal isOpen={isModalOpen} onClose={handleModalClose} />

      <AuditTrailModal
        open={audit.target !== null}
        onClose={audit.close}
        filter={{ resourceType: AUDIT_RESOURCE.LEGAL_ENTITY, resourceId: audit.target?.id ?? '' }}
        subject={audit.target?.name}
      />

      <AlertModal
        variant="danger"
        title={t('K_MODAL_DEACTIVATE_ENTITY', 'Deactivate Legal Entity')}
        description={t('K_CONFIRM_DEACTIVATE_ENTITY', 'Are you sure you want to deactivate "{name}"? The legal entity will no longer be active.').replace('{name}', deactivateItem?.name ?? '')}
        isOpen={deactivateItem !== null}
        onClose={() => setDeactivateItem(null)}
        primaryLabel={t('K_DEACTIVATE', 'Deactivate')}
        onPrimaryClick={() => {
          if (deactivateItem) deactivateMutation.mutate(deactivateItem.id);
        }}
        loading={deactivateMutation.isPending}
      />
    </div>
  );
}
